import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import {
  ApiError,
  getSeo,
  getSeoPages,
  qk,
  startSeoScan,
  type SeoCheckResult,
  type SeoPage,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import { LoadingBlock } from '../components/Spinner.js';
import Table from '../components/Table.js';
import { useActivePid } from '../components/Layout.js';
import { formatDateTime, formatNumber, truncateMiddle } from '../lib/format.js';
import {
  buildSeoIssueRows,
  seoCheckLabel,
  seoScoreTone,
  totalAffectedPages,
  type SeoIssueRow,
  type SeoScoreTone,
} from '../lib/seo-checks.js';

/**
 * §24.7 SEO — tarayıcının (`tools/seo-crawler`) son taramasının paneli.
 *
 * Dört parça: site skoru kartı, ceza puanına göre sıralı sorun tablosu, sayfa detay
 * çekmecesi ve "Yeniden tara" düğmesi. Sıralama ve renk eşiği SAF modüldedir
 * (`lib/seo-checks.ts`, ünite testli); burada yalnızca DOM işi yapılır — Isı Haritası
 * sayfasıyla aynı iş bölümü.
 *
 * Görsel dil panelin geri kalanıyla birebir: beyaz başlık şeridi → beyaz istatistik
 * şeridi → #fafafa zeminde px-6 py-8 içerik.
 *
 * KARAR (§24.1): burada HIZ metriği gösterilmez. Tek zamanlama SLOW_TTFB'dir ve o da
 * crawler'ın kendi isteğinindir; sayfa hızının tek gerçek kaynağı RUM'dur.
 */

/** §24.7 — çekmece bir seferde en çok bu kadar sayfa çeker (uç üst sınırı 200). */
const DRAWER_PAGE_LIMIT = 50;

/** §24.7 renk eşiği → sınıflar. Eşik mantığı `seoScoreTone`'da, boyama burada. */
const TONE_STYLES: Record<SeoScoreTone, { text: string; bg: string; ring: string; label: string }> = {
  good: { text: 'text-emerald-600', bg: 'bg-[#ecfdf5]', ring: 'ring-emerald-200', label: 'iyi' },
  warn: { text: 'text-amber-600', bg: 'bg-[#fffbeb]', ring: 'ring-amber-200', label: 'geliştirilebilir' },
  bad: { text: 'text-red-600', bg: 'bg-[#fef2f2]', ring: 'ring-red-200', label: 'kritik' },
  none: { text: 'text-[#9ca3af]', bg: 'bg-[#f9fafb]', ring: 'ring-[#e5e7eb]', label: 'ölçüm yok' },
};

/** §24.3 — 0 = timeout; 2xx dışı satırlar skora girmez ama çekmecede görünür. */
function statusTone(status: number): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 0) return 'danger';
  if (status >= 200 && status < 300) return 'success';
  if (status >= 300 && status < 400) return 'warning';
  if (status >= 400) return 'danger';
  return 'neutral';
}

function statusLabel(status: number): string {
  return status === 0 ? 'zaman aşımı' : String(status);
}

/**
 * §24.4 `detail` serbest biçimlidir ({ len: 72 }, { ratio, missing, total },
 * { referrers: [...] } …). Panel onu YORUMLAMAZ, okunur hâle getirir: anahtar → değer.
 * Yorumlamaya kalkmak her yeni kontrolde paneli güncellemeyi zorunlu kılardı.
 */
function detailPairs(detail: Record<string, unknown> | undefined): { key: string; value: string }[] {
  if (detail === undefined || detail === null) return [];
  return Object.entries(detail).map(([key, value]) => ({
    key,
    value:
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
        ? String(value)
        : JSON.stringify(value),
  }));
}

/* -------------------------------------------------------------------------- */
/* Çekmece                                                                     */
/* -------------------------------------------------------------------------- */

function CheckResultRow({ check }: { check: SeoCheckResult }) {
  const pairs = detailPairs(check.detail);
  return (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1 py-1">
      <span className="text-[12px] font-medium text-[#374151]">{seoCheckLabel(check.id)}</span>
      <span className="font-mono text-[11px] text-[#9ca3af]">{check.id}</span>
      <Badge tone={check.penalty > 0 ? 'warning' : 'neutral'} size="sm" title="§24.4 ceza puanı">
        −{formatNumber(check.penalty)}
      </Badge>
      {pairs.map((pair) => (
        <span key={pair.key} className="text-[11px] text-[#6b7280]">
          {pair.key}: <span className="num text-[#374151]">{pair.value}</span>
        </span>
      ))}
    </li>
  );
}

function PageDetail({ page }: { page: SeoPage }) {
  return (
    <li data-testid="seo-sayfa-satiri" className="px-5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-[#111827]" title={page.url}>
          {page.url}
        </span>
        <Badge tone={statusTone(page.status)} size="sm">
          {statusLabel(page.status)}
        </Badge>
        <span className="num text-[11px] text-[#9ca3af]">
          {/* NULL "ölçülemedi" demektir; 0 ms yazmak "çok hızlı" diye okunurdu. */}
          TTFB {page.ttfb_ms === null ? '—' : `${formatNumber(page.ttfb_ms)} ms`}
        </span>
        <span className="num text-[11px] font-semibold text-[#374151]">skor {page.score}</span>
      </div>

      {/* §24.7 — sayfanın TÜM check sonuçları (penalty + detail), yalnız seçili olan değil. */}
      {page.checks.length > 0 ? (
        <ul className="mt-1.5 divide-y divide-[#f9fafb] border-t border-[#f3f4f6] pt-1">
          {page.checks.map((check, index) => (
            <CheckResultRow key={`${check.id}-${index}`} check={check} />
          ))}
        </ul>
      ) : (
        <p className="mt-1 text-[12px] text-[#9ca3af]">Bu sayfada kontrol bulgusu yok.</p>
      )}
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* Sayfa                                                                       */
/* -------------------------------------------------------------------------- */

export default function Seo() {
  const pid = useActivePid();
  const queryClient = useQueryClient();
  /** Çekmecede açık olan kontrol (null = kapalı). */
  const [openCheck, setOpenCheck] = useState<string | null>(null);

  const seo = useQuery({
    queryKey: qk.seo(pid),
    queryFn: ({ signal }) => getSeo(pid, signal),
    /*
     * Tarama SÜRERKEN panel kendini tazeler: koşu ~3,5 dakikadır (§24.7) ve bittiğinde
     * kullanıcının sayfayı elle yenilemesi gerekmemeli. Tarama yokken yoklama KAPALIDIR —
     * boşta duran bir sekme sunucuya gereksiz istek atmaz.
     */
    refetchInterval: (query) => (query.state.data?.running === true ? 15_000 : false),
  });

  /*
   * Çekmece verisi ANCAK açıldığında çekilir: sorun tablosu zaten summary'den dolar
   * (§24.5 KARARI — denormalize summary), sayfa satırları yalnız detay için gerekir.
   */
  const pages = useQuery({
    queryKey: qk.seoPages(pid, openCheck ?? '', DRAWER_PAGE_LIMIT),
    queryFn: ({ signal }) =>
      getSeoPages(pid, { check: openCheck ?? undefined, limit: DRAWER_PAGE_LIMIT }, signal),
    enabled: openCheck !== null,
  });

  const scan = useMutation({
    mutationFn: () => startSeoScan(pid),
    onSuccess: () => {
      // Tarama kuyrukta: özet yenilenince `running` true olur ve düğme kilitlenir.
      void queryClient.invalidateQueries({ queryKey: qk.seo(pid) });
    },
  });

  const data = seo.data;
  const scanRecord = data?.scan ?? null;
  const issues: SeoIssueRow[] = buildSeoIssueRows(scanRecord?.summary, data?.samples);
  const tone = seoScoreTone(scanRecord?.score);
  const styles = TONE_STYLES[tone];
  const running = data?.running === true;

  /*
   * 409 = "zaten sürüyor" (§24.7). Ayrım DURUM KODUNDAN yapılır: sunucu mesajı
   * değişse bile kullanıcı doğru cümleyi görür. 503 ise migration eksikliğidir ve
   * sunucunun kendi açıklayıcı mesajı gösterilir.
   */
  const scanError = scan.error instanceof ApiError ? scan.error : null;
  const scanMessage =
    scanError?.status === 409
      ? 'Tarama sürüyor — bitince skor kendiliğinden güncellenir.'
      : scanError !== null
        ? scanError.message
        : scan.isSuccess
          ? 'Tarama kuyruğa alındı; sonuç birkaç dakika içinde görünür.'
          : null;
  const scanMessageTone = scanError !== null && scanError.status !== 409 ? 'text-red-600' : 'text-[#6b7280]';

  const isEmpty = !seo.isLoading && !seo.isError && scanRecord === null;
  const openIssue = issues.find((row) => row.id === openCheck) ?? null;

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="seo-sayfasi">
      {/* başlık */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center gap-2">
          <h1 className="font-grotesk text-[18px] font-bold text-[#111827]">SEO</h1>
          <BilgiDugmesi testId="seo-bilgi">Tarayıcı sitenizi kendi gezer (§24.1): eksik meta, kırık iç link, beklenmedik noindex gibi 'kullanıcı hiç gelmeden görülebilen' kusurları bulur. Sayfa HIZI burada ölçülmez — hızın tek kaynağı RUM'dur.</BilgiDugmesi>
          <span className="ml-1 hidden text-[13px] text-[#9ca3af] md:inline">
            Son tamamlanmış tarama · sorunlar ceza puanına göre sıralı
          </span>
        </div>

        <div className="flex items-center gap-3">
          {scanMessage !== null ? (
            <span className={`text-[12px] ${scanMessageTone}`}>{scanMessage}</span>
          ) : null}
          <button
            type="button"
            data-testid="seo-yeniden-tara"
            className="btn-primary"
            // Sürmekte olan tarama varken düğme kilitlidir: tıklama zaten 409 dönerdi,
            // kullanıcıyı bilerek başarısız olacak bir eyleme sürüklemeyelim (§24.7).
            disabled={scan.isPending || running}
            onClick={() => scan.mutate()}
          >
            <RefreshCw
              aria-hidden="true"
              className={`h-3.5 w-3.5 ${scan.isPending ? 'animate-spin' : ''}`}
            />
            {running ? 'Tarama sürüyor' : 'Yeniden tara'}
          </button>
        </div>
      </div>

      {/* istatistikler */}
      <div className="flex flex-wrap items-center gap-8 border-b border-[#e5e7eb] bg-white px-6 py-2.5">
        {seo.isLoading ? (
          <span className="animate-pulse text-[12px] text-[#9ca3af]">Veri yükleniyor…</span>
        ) : (
          [
            { label: 'Site skoru', value: scanRecord?.score === null || scanRecord === null ? '—' : formatNumber(scanRecord.score) },
            { label: 'Taranan sayfa', value: formatNumber(scanRecord?.pages ?? 0) },
            { label: 'Sorun türü', value: formatNumber(issues.length) },
            /*
             * DİKKAT: bu sayı TEKİL sayfa sayısı DEĞİLDİR — summary'deki tüm check
             * sayımlarının toplamıdır. Aynı sayfa hem TITLE_MISSING hem DESC_LEN
             * taşıyorsa iki kez, site geneli checkler (SITEMAP_MISSING/ROBOTS_MISSING)
             * hiç sayfaya karşılık gelmeden birer kez sayılır; toplam bu yüzden
             * "Taranan sayfa"yı aşabilir. Etiket "Etkilenen sayfa" olsaydı kullanıcı
             * onu tekil sayfa sanardı. Tekil sayım summary'den TÜRETİLEMEZ (§24.5
             * denormalize karar) — o yüzden gösterilen büyüklük dürüstçe adlandırılır.
             */
            { label: 'Toplam bulgu', value: formatNumber(totalAffectedPages(issues)) },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-2">
              <span className="text-[12px] text-[#9ca3af]">{s.label}</span>
              <span className="num text-[13px] font-semibold text-[#111827]">{s.value}</span>
            </div>
          ))
        )}
      </div>

      {/* içerik */}
      <div className="space-y-6 px-6 py-8">
        {seo.isLoading ? <LoadingBlock label="SEO taraması yükleniyor…" /> : null}

        {seo.isError ? (
          <ErrorState
            title="SEO taraması alınamadı"
            description={(seo.error as Error).message}
            onRetry={() => void seo.refetch()}
          />
        ) : null}

        {isEmpty ? (
          /*
           * §24.7 boş durum — hiç TAMAMLANMIŞ tarama yok.
           *
           * İlk tarama SÜRÜYORSA çağrı gösterilmez: `finished_at IS NULL` satır varken
           * POST kesin olarak 409 döner (§24.7), yani düğme kullanıcıyı bilerek
           * başarısız olacak bir tıklamaya çağırırdı. Üstteki "Yeniden tara" düğmesi
           * zaten aynı gerekçeyle kilitlidir; iki yol aynı kararı vermelidir.
           */
          <EmptyState
            title={running ? 'İlk tarama sürüyor' : 'Henüz SEO taraması yok'}
            description={
              running
                ? 'Tarayıcı sitenizi geziyor. Koşu birkaç dakika sürer; bitince skor ve sorun tablosu kendiliğinden görünür.'
                : 'Tarayıcı sitenizi gezip eksik meta, kırık iç link ve beklenmedik noindex kusurlarını çıkarır. İlk tarama birkaç dakika sürer.'
            }
            {...(running
              ? {}
              : { actionLabel: 'İlk taramayı başlat', onAction: () => scan.mutate() })}
          />
        ) : null}

        {scanRecord !== null ? (
          <>
            {/* 1) site skoru kartı (§24.7) */}
            <Card
              title="Site skoru"
              subtitle="100 üzerinden — sayfa skorlarının ortalaması eksi site cezaları (§24.4)"
              actions={
                <span className="text-[12px] text-[#9ca3af]">tarama #{scanRecord.id}</span>
              }
            >
              <div className="flex flex-wrap items-center gap-6">
                <div
                  data-testid="seo-skor"
                  data-tone={tone}
                  className={`flex h-24 w-24 shrink-0 flex-col items-center justify-center rounded-2xl ring-1 ${styles.bg} ${styles.ring}`}
                >
                  <span className={`num font-grotesk text-[30px] font-bold leading-none ${styles.text}`}>
                    {scanRecord.score === null ? '—' : scanRecord.score}
                  </span>
                  <span className={`mt-1 text-[11px] ${styles.text}`}>{styles.label}</span>
                </div>

                <dl className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-[13px]">
                  <dt className="text-[#9ca3af]">Tarama zamanı</dt>
                  <dd className="num text-[#111827]">{formatDateTime(scanRecord.finished_at)}</dd>
                  <dt className="text-[#9ca3af]">Başlangıç</dt>
                  <dd className="num text-[#111827]">{formatDateTime(scanRecord.started_at)}</dd>
                  <dt className="text-[#9ca3af]">Taranan sayfa</dt>
                  <dd className="num text-[#111827]">{formatNumber(scanRecord.pages)}</dd>
                </dl>
              </div>
            </Card>

            {/* 2) sorun tablosu (§24.7 — ceza puanına göre azalan) */}
            <div data-testid="seo-sorun-tablosu">
              <Card
                title="Sorunlar"
                subtitle="Ceza puanına göre azalan; satıra tıklayın — etkilenen sayfalar açılır"
                flush
              >
                <Table
                  rows={issues}
                  rowKey={(row) => row.id}
                  onRowClick={(row) => setOpenCheck(row.id === openCheck ? null : row.id)}
                  isRowActive={(row) => row.id === openCheck}
                  stickyHeader
                  emptyTitle="Bu taramada sorun bulunamadı"
                  emptyDescription="Kontrol kataloğundaki (§24.4) hiçbir kusur tetiklenmedi."
                  columns={[
                    {
                      key: 'label',
                      header: 'Kontrol',
                      render: (row) => (
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-[#111827]">{row.label}</span>
                            <span className="font-mono text-[11px] text-[#9ca3af]">{row.id}</span>
                            {row.scope === 'site' ? (
                              <Badge tone="neutral" size="sm" title="Sayfaya değil taramaya yazılır">
                                site geneli
                              </Badge>
                            ) : null}
                          </div>
                          {row.description !== '' ? (
                            <p className="mt-0.5 text-[12px] text-[#9ca3af]">{row.description}</p>
                          ) : null}
                        </div>
                      ),
                    },
                    {
                      key: 'penalty',
                      header: 'Ceza',
                      align: 'right',
                      className: 'w-20',
                      render: (row) => (
                        <span className={row.penalty > 0 ? 'text-[#111827]' : 'text-[#9ca3af]'}>
                          {row.penalty > 0 ? `−${formatNumber(row.penalty)}` : '0'}
                        </span>
                      ),
                    },
                    {
                      key: 'pages',
                      header: 'Etkilenen sayfa',
                      align: 'right',
                      className: 'w-32',
                      render: (row) => formatNumber(row.pages),
                    },
                    {
                      key: 'samples',
                      header: 'Örnek URL',
                      cellClassName: 'hidden lg:table-cell',
                      render: (row) =>
                        row.samples.length === 0 ? (
                          <span className="text-[12px] text-[#9ca3af]">
                            {row.scope === 'site' ? 'site geneli — tek bulgu' : '—'}
                          </span>
                        ) : (
                          <ul className="space-y-0.5">
                            {row.samples.slice(0, 3).map((url) => (
                              <li
                                key={url}
                                className="truncate font-mono text-[11px] text-[#6b7280]"
                                title={url}
                              >
                                {truncateMiddle(url, 52)}
                              </li>
                            ))}
                          </ul>
                        ),
                    },
                  ]}
                />
              </Card>
            </div>
          </>
        ) : null}
      </div>

      {/* 3) sayfa detay çekmecesi (§24.7) — sağdan açılan katman */}
      {openCheck !== null ? (
        <div
          role="presentation"
          className="fixed inset-0 z-50 flex justify-end bg-black/30"
          onClick={(event) => {
            if (event.target === event.currentTarget) setOpenCheck(null);
          }}
        >
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="SEO sayfa detayı"
            data-testid="seo-sayfa-cekmecesi"
            data-check={openCheck}
            className="flex h-full w-[min(760px,94vw)] flex-col border-l border-[#e5e7eb] bg-white shadow-xl"
          >
            <header className="flex items-start justify-between gap-3 border-b border-[#e5e7eb] px-5 py-3.5">
              <div className="min-w-0">
                <h2 className="truncate text-[14px] font-semibold text-[#111827]">
                  {openIssue?.label ?? seoCheckLabel(openCheck)}
                </h2>
                <p className="mt-0.5 text-[12px] text-[#9ca3af]">
                  <span className="font-mono">{openCheck}</span>
                  {openIssue !== null ? ` · ${formatNumber(openIssue.pages)} sayfa etkileniyor` : ''}
                  {/*
                    §33.5 — tavan cümlesi KOŞULLUDUR. Eskiden koşulsuz basılıyordu:
                    12 satırlık bir sonuçta da "en çok 50 satır" yazıyor, gerçekten
                    kırpıldığı durumu ise ayırt edilemez kılıyordu (denetim bulgusu C4).
                  */}
                  {pages.data?.truncated === true
                    ? ` · ilk ${formatNumber(pages.data.limit)} satır gösteriliyor (kırpıldı)`
                    : ''}
                </p>
              </div>
              <button
                type="button"
                className="btn-ghost"
                aria-label="Çekmeceyi kapat"
                onClick={() => setOpenCheck(null)}
              >
                <X aria-hidden="true" className="h-3.5 w-3.5 text-[#9ca3af]" />
                Kapat
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {pages.isLoading ? <LoadingBlock label="Sayfalar yükleniyor…" /> : null}

              {pages.isError ? (
                <div className="p-5">
                  <ErrorState
                    title="Sayfalar alınamadı"
                    description={(pages.error as Error).message}
                    onRetry={() => void pages.refetch()}
                  />
                </div>
              ) : null}

              {!pages.isLoading && !pages.isError && (pages.data?.items.length ?? 0) === 0 ? (
                <EmptyState
                  compact
                  title="Bu kontrol için sayfa satırı yok"
                  description="Site geneli kontroller (sitemap, robots.txt, kırık iç link) taramaya yazılır, tek tek sayfalara değil."
                />
              ) : null}

              <ul className="divide-y divide-[#f3f4f6]">
                {(pages.data?.items ?? []).map((page) => (
                  <PageDetail key={page.id} page={page} />
                ))}
              </ul>
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  );
}
